import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';

import { DateInput } from '@/components/form/date-input';
import { GlCodeTypeahead } from '@/components/search/gl-code-typeahead';
import { TargetTypeahead, targetRefToLineFields, type TargetRef } from '@/components/search/target-typeahead';
import { ActivityIndicator, Button, Modal, Text } from '@/components/ui';
import { useCreateJournalEntry, useJournalEntry, useUpdateJournalEntry } from '@/hooks/use-accounting';
import type { GlCode, JournalLineInput } from '@/types/models';
import { formatBusinessDateIso, parseDateToMs, todayBusinessIso } from '@/utils/date-utils';
import { formatMoneyCents, parseMoneyToCents, reinterpretAsCents } from '@/utils/format-money';

export interface ManualJournalEntryModalProps {
	visible: boolean;
	associationId: string;
	onDismiss: () => void;
	onPosted?: (count: number) => void;
	/**
	 * When set, the modal edits an existing manual/opening journal entry instead
	 * of composing new ones. It loads the entry, prefills a single grid, and
	 * saves via `PUT /journal-entries/:id`. (#357)
	 */
	entryId?: string;
}

/** Frozen sub-ledger pointers carried through an edit when the user doesn't
 *  re-pick a target (the target typeahead starts blank on edit to avoid the
 *  clear-on-mount race; these preserve the original AR/AP/bank linkage). */
interface FrozenTarget {
	accountId?: string;
	bankAccountId?: string;
	vendorId?: string;
}

interface DraftLine {
	key: string;
	glCodeId: string | undefined;
	/** Seed text for the GL typeahead on edit prefill (keeps the committed code
	 *  visible so the typeahead's clear-on-empty effect doesn't wipe it). */
	glText?: string;
	target: TargetRef | null;
	frozenTarget?: FrozenTarget;
	debit: string;
	credit: string;
	memo: string;
}

interface DraftEntry {
	key: string;
	postedOn: string; // YYYY-MM-DD
	memo: string;
	lines: DraftLine[];
}

const isoToUtcMs = (iso: string): number | null =>
	/^\d{4}-\d{2}-\d{2}$/.test(iso) ? parseDateToMs(iso) : null;

const newLine = (): DraftLine => ({
	key: crypto.randomUUID(),
	glCodeId: undefined,
	target: null,
	debit: '',
	credit: '',
	memo: '',
});

const isLineEmpty = (l: DraftLine): boolean =>
	!l.glCodeId && !l.target && !l.debit && !l.credit && !l.memo;

/**
 * Ensure exactly one trailing empty line. Excel-style: as soon as the user
 * touches the last row, a fresh empty row appears below; if they wipe a row
 * back to empty and a trailing empty row already exists, we collapse to one.
 */
const reconcileTrailingBlank = (lines: DraftLine[]): DraftLine[] => {
	if (lines.length === 0) return [newLine()];
	const last = lines[lines.length - 1];
	if (!isLineEmpty(last)) return [...lines, newLine()];
	// Drop redundant trailing blanks (more than one in a row at the tail).
	let end = lines.length - 1;
	while (end > 0 && isLineEmpty(lines[end - 1])) end--;
	if (end === lines.length - 1) return lines;
	return lines.slice(0, end + 1);
};

const newEntry = (): DraftEntry => ({
	key: crypto.randomUUID(),
	postedOn: todayBusinessIso(),
	memo: '',
	lines: [newLine()],
});

const sumLineCents = (lines: DraftLine[], side: 'debit' | 'credit'): number =>
	lines.reduce((s, ln) => {
		const cents = parseMoneyToCents(side === 'debit' ? ln.debit : ln.credit);
		return s + (Number.isFinite(cents) && cents > 0 ? cents : 0);
	}, 0);

/**
 * Manual journal entry composer. Spreadsheet-style grid: every cell is a bare
 * input inside a thin-bordered table. The last line of every entry is always
 * blank — typing into it spawns a fresh blank below. Tab moves cell to cell
 * (browser-native focus advance); on a GL cell, Tab also commits the
 * highlighted match before advancing (see `GlCodeTypeahead`).
 *
 * Each entry posts through `POST /journal-entries` with `source = 'manual'`
 * so projections fire downstream. Entries submit sequentially; one failure
 * does not roll back prior posts.
 */
export const ManualJournalEntryModal: React.FC<ManualJournalEntryModalProps> = ({
	visible, associationId, onDismiss, onPosted, entryId,
}) => {
	const isEdit = !!entryId;
	const [entries, setEntries] = useState<DraftEntry[]>(() => (entryId ? [] : [newEntry()]));
	const [hydrated, setHydrated] = useState(!entryId);
	const [submitting, setSubmitting] = useState(false);
	const createEntry = useCreateJournalEntry();
	const updateEntryMutation = useUpdateJournalEntry();
	const { data: loadedEntry } = useJournalEntry(entryId);

	// Prefill a single grid from the loaded entry. Amounts → dollar strings; the
	// GL cell is seeded with its committed code; the sub-ledger pointer is frozen
	// (see FrozenTarget) rather than pushed into the target typeahead, which would
	// wipe itself on the null→value transition. Runs once when the entry arrives.
	useEffect(() => {
		if (!entryId || hydrated || !loadedEntry) return;
		const lines: DraftLine[] = loadedEntry.lines.map((l) => ({
			key: crypto.randomUUID(),
			glCodeId: l.glCodeId,
			glText: l.glCode ? `${l.glCode} — ${l.glCodeName ?? ''}` : undefined,
			target: null,
			frozenTarget: {
				accountId: l.accountId,
				bankAccountId: l.bankAccountId,
				vendorId: l.vendorId,
			},
			debit: l.debitCents > 0 ? (l.debitCents / 100).toFixed(2) : '',
			credit: l.creditCents > 0 ? (l.creditCents / 100).toFixed(2) : '',
			memo: l.memo ?? '',
		}));
		setEntries([{
			key: crypto.randomUUID(),
			postedOn: formatBusinessDateIso(loadedEntry.postedOn),
			memo: loadedEntry.memo ?? '',
			lines: reconcileTrailingBlank(lines),
		}]);
		setHydrated(true);
	}, [entryId, hydrated, loadedEntry]);

	const isDirty = useMemo(() => entries.some((e) =>
		!!e.memo || e.lines.some((l) => !isLineEmpty(l)),
	), [entries]);

	const anyUnbalanced = useMemo(() => entries.some((e) => {
		const d = sumLineCents(e.lines, 'debit');
		const c = sumLineCents(e.lines, 'credit');
		return d === 0 || d !== c;
	}), [entries]);

	const reset = () => setEntries([newEntry()]);

	// The discard confirm is a nested @/components/ui Modal (not Alert.alert):
	// AlertProvider's web dialog portals BENEATH this RN-Modal-based modal, so an
	// Alert here paints invisibly behind us and closing appears to do nothing.
	// See memory alertprovider-dialog-renders-behind-flexmodal.
	const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
	// Inline error banner — an Alert.alert here would portal behind this modal
	// (same stacking issue as the discard confirm), so validation/save errors
	// show in-panel instead.
	const [errorMsg, setErrorMsg] = useState<string | null>(null);

	const tryDismiss = () => {
		if (submitting) return;
		if (isDirty) { setConfirmDiscardOpen(true); return; }
		onDismiss();
	};

	const confirmDiscard = () => {
		setConfirmDiscardOpen(false);
		reset();
		onDismiss();
	};

	const updateEntry = (entryKey: string, patch: Partial<DraftEntry>) => {
		setEntries((prev) => prev.map((e) => (e.key === entryKey ? { ...e, ...patch } : e)));
	};

	const updateLine = useCallback((entryKey: string, lineKey: string, patch: Partial<DraftLine>) => {
		setEntries((prev) => prev.map((e) => {
			if (e.key !== entryKey) return e;
			const lines = e.lines.map((l) => (l.key === lineKey ? { ...l, ...patch } : l));
			return { ...e, lines: reconcileTrailingBlank(lines) };
		}));
	}, []);

	const addEntry = () => setEntries((prev) => [...prev, newEntry()]);
	const removeEntry = (entryKey: string) =>
		setEntries((prev) => (prev.length <= 1 ? prev : prev.filter((e) => e.key !== entryKey)));

	const validateEntry = (entry: DraftEntry, index: number): string | null => {
		const postedMs = isoToUtcMs(entry.postedOn);
		if (postedMs == null) return `Entry ${index + 1}: enter the posted date as YYYY-MM-DD.`;
		const usable = entry.lines.filter((l) => l.glCodeId && (l.debit || l.credit));
		if (usable.length < 2) return `Entry ${index + 1}: needs at least two lines with a GL code and a debit or credit.`;
		const debits = sumLineCents(entry.lines, 'debit');
		const credits = sumLineCents(entry.lines, 'credit');
		if (debits === 0) return `Entry ${index + 1}: total amount must be greater than zero.`;
		if (debits !== credits) {
			return `Entry ${index + 1}: debits (${formatMoneyCents(debits)}) must equal credits (${formatMoneyCents(credits)}).`;
		}
		for (const ln of usable) {
			const debit = parseMoneyToCents(ln.debit);
			const credit = parseMoneyToCents(ln.credit);
			if (debit > 0 && credit > 0) return `Entry ${index + 1}: each line must be debit OR credit, not both.`;
		}
		return null;
	};

	// Flatten a draft entry's usable lines into JournalLineInput. On edit, when
	// the user didn't re-pick a target we fall back to the frozen sub-ledger
	// pointer so AR/AP/bank linkage survives the round-trip.
	const buildLines = (e: DraftEntry): JournalLineInput[] =>
		e.lines
			.filter((l) => l.glCodeId && (l.debit || l.credit))
			.map((l, idx) => ({
				glCodeId: l.glCodeId!,
				debitCents: parseMoneyToCents(l.debit) || 0,
				creditCents: parseMoneyToCents(l.credit) || 0,
				memo: l.memo.trim() || undefined,
				sortOrder: idx,
				...(l.target ? targetRefToLineFields(l.target) : (l.frozenTarget ?? {})),
			}));

	const onSubmit = async () => {
		setErrorMsg(null);
		for (let i = 0; i < entries.length; i++) {
			const err = validateEntry(entries[i], i);
			if (err) { setErrorMsg(err); return; }
		}
		if (isEdit && entryId) {
			const e = entries[0];
			setSubmitting(true);
			try {
				await updateEntryMutation.mutateAsync({
					id: entryId,
					body: {
						postedOn: isoToUtcMs(e.postedOn)!,
						memo: e.memo.trim() || undefined,
						lines: buildLines(e),
					},
				});
				setSubmitting(false);
				onPosted?.(1);
				onDismiss();
			} catch (err) {
				setSubmitting(false);
				setErrorMsg((err as Error).message);
			}
			return;
		}
		setSubmitting(true);
		const failures: { index: number; message: string }[] = [];
		let posted = 0;
		for (let i = 0; i < entries.length; i++) {
			const e = entries[i];
			try {
				await createEntry.mutateAsync({
					associationId,
					postedOn: isoToUtcMs(e.postedOn)!,
					memo: e.memo.trim() || undefined,
					source: 'manual',
					lines: buildLines(e),
				});
				posted++;
			} catch (err) {
				failures.push({ index: i, message: (err as Error).message });
			}
		}
		setSubmitting(false);
		if (failures.length === 0) {
			onPosted?.(posted);
			reset();
			onDismiss();
			return;
		}
		const detail = failures.map((f) => `Entry ${f.index + 1}: ${f.message}`).join('\n');
		const heading = posted > 0 ? `Posted ${posted}, ${failures.length} failed` : 'No entries posted';
		setErrorMsg(`${heading}\n${detail}`);
		if (posted > 0) onPosted?.(posted);
	};

	const onGlSelect = useCallback((entryKey: string, lineKey: string, gl: GlCode | null) => {
		updateLine(entryKey, lineKey, { glCodeId: gl?.id ?? undefined });
	}, [updateLine]);

	const onTargetSelect = useCallback((entryKey: string, lineKey: string, target: TargetRef | null) => {
		updateLine(entryKey, lineKey, { target });
	}, [updateLine]);

	return (
		<>
		<Modal visible={visible} onDismiss={tryDismiss} onSubmit={onSubmit} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>{isEdit ? 'Edit journal entry' : 'New journal entry'}</Text>
				<Pressable onPress={tryDismiss} hitSlop={8}>
					<Ionicons name="close" size={22} color="#78909c" />
				</Pressable>
			</View>
			<Text style={styles.help}>
				Tab moves between cells. The last line is always blank — start typing it and a new line appears below.
			</Text>

			{errorMsg && (
				<View style={styles.errorBanner} testID="je-error-banner">
					<Ionicons name="alert-circle" size={16} color="#c62828" />
					<Text style={styles.errorText}>{errorMsg}</Text>
				</View>
			)}

			{!hydrated ? (
				<View style={styles.loading}><ActivityIndicator /></View>
			) : (
				<ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
					{entries.map((entry, entryIdx) => {
						const debits = sumLineCents(entry.lines, 'debit');
						const credits = sumLineCents(entry.lines, 'credit');
						const diff = debits - credits;
						const balanced = debits > 0 && diff === 0;
						return (
							<View key={entry.key} style={styles.grid}>
								{/* Title row */}
								<View style={[styles.row, styles.titleRow]}>
									<Text style={styles.entryLabel}>Entry {entryIdx + 1}</Text>
									{entries.length > 1 && (
										<Pressable onPress={() => removeEntry(entry.key)} hitSlop={6}>
											<Ionicons name="trash-outline" size={14} color="#c62828" />
										</Pressable>
									)}
								</View>

								{/* Meta header */}
								<View style={[styles.row, styles.metaHeader]}>
									<View style={[styles.cell, styles.colDate]}>
										<Text style={styles.headerLabel}>Posted</Text>
									</View>
									<View style={[styles.cell, styles.colMeta, styles.colLast]}>
										<Text style={styles.headerLabel}>Memo</Text>
									</View>
								</View>
								<View style={styles.row}>
									<View style={[styles.cell, styles.colDate]}>
										<DateInput
											label="Posted on"
											value={(() => {
												const m = entry.postedOn.match(/^(\d{4})-(\d{2})-(\d{2})$/);
												return m ? new Date(`${entry.postedOn}T05:00:00Z`) : null;
											})()}
											onChange={(d) => {
												const s = d
													? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
													: '';
												updateEntry(entry.key, { postedOn: s });
											}}
											// Compact fit for this dense grid cell — the default is a full-size
											// form field (16px text, 56px tall) that clips the date at 140px.
											style={{ fontSize: 14, padding: 10, paddingRight: 34, minHeight: 40, borderRadius: 6 }}
										/>
									</View>
									<View style={[styles.cell, styles.colMeta, styles.colLast]}>
										<RNTextInput
											value={entry.memo}
											onChangeText={(v) => updateEntry(entry.key, { memo: v })}
											placeholder="What's this entry for?"
											placeholderTextColor="#b0bec5"
											style={styles.input}
										/>
									</View>
								</View>

								{/* Line table header */}
								<View style={[styles.row, styles.tableHeader]}>
									<View style={[styles.cell, styles.colGl]}>
										<Text style={styles.headerLabel}>GL code</Text>
									</View>
									<View style={[styles.cell, styles.colTarget]}>
										<Text style={styles.headerLabel}>Target</Text>
									</View>
									<View style={[styles.cell, styles.colMemo]}>
										<Text style={styles.headerLabel}>Memo</Text>
									</View>
									<View style={[styles.cell, styles.colAmt]}>
										<Text style={[styles.headerLabel, styles.right]}>Debit</Text>
									</View>
									<View style={[styles.cell, styles.colAmt, styles.colLast]}>
										<Text style={[styles.headerLabel, styles.right]}>Credit</Text>
									</View>
								</View>

								{entry.lines.map((line) => (
									<View key={line.key} style={styles.row}>
										<View style={[styles.cell, styles.colGl, styles.glCell]}>
											<GlCodeTypeahead
												value={line.glCodeId}
												seedText={line.glText}
												onSelect={(g) => onGlSelect(entry.key, line.key, g)}
												inputStyle={styles.bareGlInput}
												placeholder="GL code…"
											/>
										</View>
										<View style={[styles.cell, styles.colTarget, styles.glCell]}>
											<TargetTypeahead
												associationId={associationId}
												value={line.target}
												onSelect={(t) => onTargetSelect(entry.key, line.key, t)}
												inputStyle={styles.bareGlInput}
												placeholder="(optional)"
											/>
										</View>
										<View style={[styles.cell, styles.colMemo]}>
											<RNTextInput
												value={line.memo}
												onChangeText={(v) => updateLine(entry.key, line.key, { memo: v })}
												placeholder=""
												placeholderTextColor="#b0bec5"
												style={styles.input}
											/>
										</View>
										<View style={[styles.cell, styles.colAmt]}>
											<RNTextInput
												value={line.debit}
												onChangeText={(v) => {
													const formatted = reinterpretAsCents(v);
													updateLine(entry.key, line.key, {
														debit: formatted,
														credit: formatted ? '' : line.credit,
													});
												}}
												placeholder="0.00"
												placeholderTextColor="#b0bec5"
												keyboardType="decimal-pad"
												style={[styles.input, styles.right]}
											/>
										</View>
										<View style={[styles.cell, styles.colAmt, styles.colLast]}>
											<RNTextInput
												value={line.credit}
												onChangeText={(v) => {
													const formatted = reinterpretAsCents(v);
													updateLine(entry.key, line.key, {
														credit: formatted,
														debit: formatted ? '' : line.debit,
													});
												}}
												placeholder="0.00"
												placeholderTextColor="#b0bec5"
												keyboardType="decimal-pad"
												style={[styles.input, styles.right]}
											/>
										</View>
									</View>
								))}

								{/* Totals row */}
								<View style={[styles.row, styles.totalsRow]}>
									<View style={[styles.cell, styles.colGl, styles.totalsPad]} />
									<View style={[styles.cell, styles.colTarget, styles.totalsPad]} />
									<View style={[styles.cell, styles.colMemo, styles.totalsPad]}>
										<Text style={[styles.totalsLabel, styles.right]}>Totals</Text>
									</View>
									<View style={[styles.cell, styles.colAmt, styles.totalsPad]}>
										<Text style={[styles.totalsAmt, styles.right]}>{formatMoneyCents(debits)}</Text>
									</View>
									<View style={[styles.cell, styles.colAmt, styles.colLast, styles.totalsPad]}>
										<Text style={[styles.totalsAmt, styles.right]}>{formatMoneyCents(credits)}</Text>
									</View>
								</View>

								{!balanced && debits + credits > 0 && (
									<View style={[styles.row, styles.offByRow]}>
										<View style={[styles.cell, styles.colGl, styles.totalsPad]} />
										<View style={[styles.cell, styles.colTarget, styles.totalsPad]} />
										<View style={[styles.cell, styles.colMemo, styles.totalsPad]} />
										<View style={[styles.cell, styles.colAmt, styles.totalsPad]} />
										<View style={[styles.cell, styles.colAmt, styles.colLast, styles.offByCell]}>
											<View style={[styles.balanceDot, styles.balanceBad]} />
											<Text style={styles.offByLabel}>Off by {formatMoneyCents(Math.abs(diff))}</Text>
										</View>
									</View>
								)}
							</View>
						);
					})}

					{!isEdit && (
						<Pressable onPress={addEntry} style={styles.addEntry}>
							<Ionicons name="add" size={14} color="#1976d2" />
							<Text style={styles.addEntryLabel}>Add another entry</Text>
						</Pressable>
					)}
				</ScrollView>
			)}

			<View style={styles.actions}>
				<Button mode="outlined" onPress={tryDismiss} disabled={submitting}>Cancel</Button>
				<Button mode="contained" onPress={onSubmit} disabled={submitting || !hydrated || anyUnbalanced}>
					{isEdit
						? (submitting ? 'Saving…' : 'Save changes')
						: (submitting ? 'Posting…' : `Post ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`)}
				</Button>
			</View>
		</Modal>
		<Modal
			visible={confirmDiscardOpen}
			onDismiss={() => setConfirmDiscardOpen(false)}
			contentContainerStyle={styles.confirmModal}
		>
			<Text style={styles.confirmTitle}>Discard {isEdit ? 'changes' : 'entries'}?</Text>
			<Text style={styles.confirmMessage}>
				You have unsaved {isEdit ? 'changes to this journal entry' : 'journal entries'}. Close anyway?
			</Text>
			<View style={styles.confirmActions}>
				<Button mode="outlined" onPress={() => setConfirmDiscardOpen(false)} testID="je-discard-keep">Keep editing</Button>
				<Button mode="contained" onPress={confirmDiscard} testID="je-discard-confirm">Discard</Button>
			</View>
		</Modal>
		</>
	);
};

const BORDER = '#cfd8dc';

const styles = StyleSheet.create({
	modal: { width: 1100, maxWidth: '98%', maxHeight: '92%' },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
	title: { fontSize: 18, fontWeight: '700', color: '#212121' },
	help: { color: '#78909c', fontSize: 12, marginBottom: 10 },

	errorBanner: {
		flexDirection: 'row', alignItems: 'flex-start', gap: 8,
		backgroundColor: '#fdecea', borderColor: '#f5c6c2', borderWidth: 1,
		borderRadius: 6, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 10,
	},
	errorText: { color: '#c62828', fontSize: 13, flex: 1 },

	confirmModal: { width: 440, maxWidth: '95%' },
	confirmTitle: { fontSize: 18, fontWeight: '700', color: '#212121', marginBottom: 8 },
	confirmMessage: { fontSize: 14, lineHeight: 20, color: '#546e7a', marginBottom: 16 },
	confirmActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },

	scroll: { maxHeight: 560 },
	scrollContent: { paddingBottom: 8, gap: 16 },
	loading: { paddingVertical: 48, alignItems: 'center' },

	grid: {
		borderWidth: 1,
		borderColor: BORDER,
		backgroundColor: '#fff',
	},
	row: {
		flexDirection: 'row',
		alignItems: 'stretch',
		borderBottomWidth: 1,
		borderBottomColor: BORDER,
		minHeight: 32,
	},
	titleRow: {
		paddingHorizontal: 8,
		paddingVertical: 6,
		backgroundColor: '#f5f7fa',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	entryLabel: { fontWeight: '700', color: '#37474f', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 },

	metaHeader: { backgroundColor: '#fafbfc' },
	tableHeader: { backgroundColor: '#fafbfc' },
	totalsRow: { backgroundColor: '#fafbfc', borderBottomWidth: 0 },
	totalsPad: { paddingVertical: 8, paddingHorizontal: 8 },
	totalsLabel: {
		fontSize: 11, fontWeight: '700', color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.4,
	},
	totalsAmt: { fontSize: 13, fontWeight: '700', color: '#212121' },

	cell: {
		borderRightWidth: 1,
		borderRightColor: BORDER,
		justifyContent: 'center',
		minHeight: 32,
		// minWidth: 0 overrides the flex-item default of min-width: auto, which
		// otherwise lets TextInput cells exceed their flex share and misalign rows.
		minWidth: 0,
		flexBasis: 0,
	},
	colLast: { borderRightWidth: 0 },
	glCell: { overflow: 'visible' },
	headerLabel: {
		fontSize: 10,
		fontWeight: '700',
		color: '#546e7a',
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		paddingHorizontal: 8,
		paddingVertical: 6,
	},

	colDate: { width: 170, flexGrow: 0, flexShrink: 0, flexBasis: 170 },
	colMeta: { flex: 1 },

	// Percentage widths (not flex) so columns align row-to-row regardless of cell content.
	colGl: { width: '30%', flexGrow: 0, flexShrink: 0, flexBasis: '30%' },
	colTarget: { width: '30%', flexGrow: 0, flexShrink: 0, flexBasis: '30%' },
	colMemo: { width: '20%', flexGrow: 0, flexShrink: 0, flexBasis: '20%' },
	colAmt: { width: '10%', flexGrow: 0, flexShrink: 0, flexBasis: '10%' },

	input: {
		paddingHorizontal: 8,
		paddingVertical: 8,
		fontSize: 13,
		color: '#212121',
		backgroundColor: 'transparent',
		minHeight: 32,
	},
	// Flatten the typeahead's own border so it merges into the cell wrapping it.
	bareGlInput: {
		borderWidth: 0,
		borderRadius: 0,
		paddingHorizontal: 8,
		paddingVertical: 8,
		fontSize: 13,
		backgroundColor: 'transparent',
		minHeight: 32,
	},
	right: { textAlign: 'right' },

	offByRow: { backgroundColor: '#fff5f5', borderBottomWidth: 0 },
	offByCell: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 6,
		paddingHorizontal: 8,
		paddingVertical: 6,
	},
	balanceDot: { width: 8, height: 8, borderRadius: 4 },
	balanceBad: { backgroundColor: '#e53935' },
	offByLabel: { fontSize: 11, fontWeight: '700', color: '#b71c1c' },

	addEntry: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
		alignSelf: 'flex-start',
		paddingHorizontal: 8,
		paddingVertical: 6,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: '#bbdefb',
		borderStyle: 'dashed',
	},
	addEntryLabel: { color: '#1976d2', fontWeight: '600', fontSize: 12 },

	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
});
